import {ScrapperResponse} from "@/model/Scrapper";
import {getUid} from "@/utils/uid";
import {DocumentData} from "firebase/firestore";
import {Image} from '@/model/Scrapper';
import type {AiWritingBrief} from "@/model/AiWritingBrief";
import type {ArticleAudienceId, ArticleDepthId, ArticleToneId, GeoTemplateId} from "@/config/WritingBrief";
import {normalizeGeoTemplate} from "@/config/WritingBrief";
import {EatAttribution, emptyEatAttribution, normalizeEatAttribution} from "@/service/extractEatAttribution";
import {normalizeArticleInvisibleText} from '@/service/articleTextFields';
import {stripLeadingHeading} from '@/service/analyzeFidelity';
import {sanitizeFragmentHtml} from '@/utils/sanitizeHtml';

export type {EatAttribution};

export class ArticleMetadata {
    keyword = '';
    // how many times user change the main keyword
    keywordChangeCounter = 0;
    aiPrompt = '';
    noAiPrompt = false;
    language_code = '';
    location_code = '';
    seoScore = 0;
    loadedSeoScore = 0; // used only for check if seo score changed
    url = '';
    // mainKeyword = ''; // used for sorting in ArticleTable (firestore uses lexicographical order)
    organicPosition = null as null | SearchPosition;
    googleAIOPosition = null as null | SearchPosition;
    chatGPTPosition = null as null | SearchPosition;

    isAnalyze = false;
    forcedEmpty = false;

    geoTemplate: GeoTemplateId = 'universal';
    /** True when the user explicitly picked a template on the create brief (blocks auto-switch after analysis). */
    geoTemplateUserPicked = false;
    tone: ArticleToneId = 'professional';
    audience: ArticleAudienceId = 'general';
    depth: ArticleDepthId = 'standard';
    secondaryKeywords: string[] = [];
    /** Path B chat choice: polish, fill, or rewrite. Empty until that step is saved. */
    improveGoal = '';
    /** H2 ids from the loaded outline, kept when the goal is rewrite. */
    sectionUids: string[] = [];
    aiWritingBrief: AiWritingBrief | null = null;

    created_at?: number = Date.now();
}

export interface SearchPosition {
    position: null | number | string; // string if "> 100"
    metadata?: {
        title?: string,
        text?: string,
        url?: string
    }
    error: null | string;
    isProcessing: boolean;
    timestamp: number;
    /** When an empty LLM answer last kept this position; a manual check within 24 h does not ask again (#30945). */
    emptyCheckedAt?: number;
}

export enum PositionSource {
    GOOGLE = "google_organic",
    GOOGLE_AIO = "google_organic_aio",
    CHAT_GPT = "llm_scrapper_chatgpt"
}

export interface ArticlePart {
    number: number;
    value: ArticlePartContent[];
}

export type ArticleMetaKey = 'metaTitle' | 'metaDescription';
export type ArticleContentKey = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
export type ArticleContentTag = 'h' | 'p';
export type ArticleQuestionTag = 'question' | 'answer';

export interface ArticlePartContent<T = string> {
    h: T;
    p: T;
    uid: number;
}

export interface ArticleContent<T = string> {
    metaTitle: T;
    metaDescription: T;
    h1: ArticlePartContent<T>[];
    h2: ArticlePartContent<T>[];
    h3: ArticlePartContent<T>[];
    h4: ArticlePartContent<T>[];
    h5: ArticlePartContent<T>[];
    h6: ArticlePartContent<T>[];
    questions: ArticleQuestion<T>[];
}

export interface ArticleQuestion<T = string> {
    question: T;
    answer: T;
    uid: number;
}

export type KeywordSource = 'main' | 'inflection' | 'token' | 'secondary' | 'custom';

export interface Keyword {
    keyword: string;
    checked: boolean;
    /** How the row entered the density list. Older saved articles may omit it. */
    source?: KeywordSource;
    /** Main keyword a density inflection belongs to. */
    of?: string;
}

export type LucyKeywordIntent = 'section' | 'faq' | 'weave';

export interface KeywordForAi {
    keyword: string;
    // from keywordType enum
    type: number;
    /** How AI Lucy uses the phrase: H2 section, FAQ block, or weave into existing text. */
    intent?: LucyKeywordIntent;
}

export type PolishState = "pending" | "done" | "failed" | "processing" | "skipped";
export type EnhanceState = "pending" | "done" | "processing";

export type BlockProposalPart = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "question";
export type BlockProposalState = "pending" | "accepted" | "kept" | "failed";
export type BlockProposalFailure = "missing-numbers" | "invented-numbers" | "unusable" | "changed-number-meaning" | "instruction-leak";
export type BlockProposalKind = "replace" | "insert";

export interface BlockProposal {
    id: string;
    part: BlockProposalPart;
    blockIndex: number;
    kind: BlockProposalKind;
    originalHeading: string;
    originalBody: string;
    proposedHeading: string;
    proposedBody: string;
    state: BlockProposalState;
    failure?: BlockProposalFailure;
    /** A few original figures are missing and nothing was invented. */
    numberWarning?: boolean;
    rejectedBody?: string;
    foundNumbers?: string[];
    missingNumbers?: string[];
    inventedNumbers?: string[];
    reason?: string;
}

export interface FillDiscardedTopic {
    topic: string;
    reason: string;
    reasonCs: string;
    reasonEn: string;
    part?: BlockProposalPart;
}

const BLOCK_PROPOSAL_PARTS = new Set<string>(["h1", "h2", "h3", "h4", "h5", "h6", "question"]);

function numberList(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.map((item) => String(item ?? "").trim()).filter((item) => item.length > 0);
}

export function normalizeFillDiscardedTopics(value: unknown): FillDiscardedTopic[] {
    if (!Array.isArray(value)) {
        return [];
    }
    const rows: FillDiscardedTopic[] = [];
    value.forEach((item) => {
        if (!item || typeof item !== "object") {
            return;
        }
        const row = item as Record<string, unknown>;
        const topic = String(row.topic || "").trim();
        const reason = String(row.reason || "").trim();
        const reasonCs = String(row.reasonCs || "").trim();
        const reasonEn = String(row.reasonEn || "").trim();
        if (!topic || !reason || !reasonCs || !reasonEn) {
            return;
        }
        const part = String(row.part || "");
        rows.push({
            topic,
            reason,
            reasonCs,
            reasonEn,
            part: BLOCK_PROPOSAL_PARTS.has(part) ? part as BlockProposalPart : undefined,
        });
    });
    return rows;
}

export function normalizeBlockProposals(value: unknown): BlockProposal[] {
    if (!Array.isArray(value)) {
        return [];
    }
    const proposals: BlockProposal[] = [];
    const seen = new Set<string>();
    value.forEach((item) => {
        if (!item || typeof item !== "object") {
            return;
        }
        const row = item as Record<string, unknown>;
        const id = String(row.id || "").trim();
        const part = String(row.part || "");
        const kind = String(row.kind || "");
        const state = String(row.state || "");
        if (!id || seen.has(id) || !BLOCK_PROPOSAL_PARTS.has(part)) {
            return;
        }
        if (kind !== "replace" && kind !== "insert") {
            return;
        }
        if (state !== "pending" && state !== "accepted" && state !== "kept" && state !== "failed") {
            return;
        }
        seen.add(id);
        const index = Number(row.blockIndex);
        const originalHeading = String(row.originalHeading || "");
        const proposedHeading = String(row.proposedHeading || "");
        const failure = row.failure === "missing-numbers"
            || row.failure === "invented-numbers"
            || row.failure === "unusable"
            || row.failure === "changed-number-meaning"
            || row.failure === "instruction-leak"
            ? row.failure
            : undefined;
        // The doc is client-writable and the body comes from a scrape and the
        // model, so it is cleaned here as well as where it renders (#30939).
        const proposedBody = state === "failed"
            ? ""
            : sanitizeFragmentHtml(stripLeadingHeading(stripLeadingHeading(String(row.proposedBody || ""), originalHeading), proposedHeading));
        const proposal: BlockProposal = {
            id,
            part: part as BlockProposalPart,
            blockIndex: Number.isFinite(index) ? index : -1,
            kind,
            originalHeading,
            originalBody: sanitizeFragmentHtml(String(row.originalBody || "")),
            proposedHeading,
            proposedBody,
            state,
        };
        if (state === "failed") {
            const resolvedFailure = failure || "unusable";
            const resolvedReason = String(row.reason || "").trim();
            if (resolvedFailure === "unusable" && (!resolvedReason || resolvedReason === "unusable")) {
                return;
            }
            proposal.failure = resolvedFailure;
            proposal.rejectedBody = sanitizeFragmentHtml(String(row.rejectedBody || ""));
            proposal.foundNumbers = numberList(row.foundNumbers);
            proposal.missingNumbers = numberList(row.missingNumbers);
            proposal.inventedNumbers = numberList(row.inventedNumbers);
            proposal.reason = resolvedReason || resolvedFailure;
        } else if (row.reason === "table-kept" || row.reason === "figures-kept") {
            proposal.reason = row.reason === "figures-kept" ? "figures-kept" : "table-kept";
        } else if (row.numberWarning === true) {
            proposal.numberWarning = true;
            proposal.foundNumbers = numberList(row.foundNumbers);
            proposal.missingNumbers = numberList(row.missingNumbers);
            proposal.inventedNumbers = numberList(row.inventedNumbers);
            proposal.reason = String(row.reason || "missing-numbers");
        }
        proposals.push(proposal);
    });
    return proposals;
}

export function getDefaultForContent<T = string>(defaultValue: string | number | boolean = '') {
    return <ArticlePartContent<T>>{
        h: defaultValue,
        p: defaultValue,
        uid: getUid()
    };
}

export function getDefaultForQuestion<T = string>(defaultValue: string | number | boolean = '') {
    return <ArticleQuestion<T>>{
        question: defaultValue,
        answer: defaultValue,
        uid: getUid()
    };
}

/**
 * Firestore update throws when any own property is undefined, including nested
 * brief fields such as intent / snippetShape / suggestedGeoTemplate.
 * Omit those keys. Array holes become null so indexes stay put.
 */
export function omitUndefinedFields(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map((item) => item === undefined ? null : omitUndefinedFields(item));
    }
    if (!value || typeof value !== 'object') {
        return value;
    }
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) {
        return value;
    }
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>)) {
        const child = (value as Record<string, unknown>)[key];
        if (child === undefined) {
            continue;
        }
        out[key] = omitUndefinedFields(child);
    }
    return out;
}

export class Article extends ArticleMetadata implements ArticleContent {

    selectedKeyword = '';
    allKeywords: Keyword[] = [];
    // Up to 20 keywords selected by user which can be used as prompts for AI
    keywordsForAi: (null | KeywordForAi[]) = null;

    metaTitle = '';
    metaDescription = '';
    h1: ArticlePartContent[] = [];
    h2: ArticlePartContent[] = [];
    h3: ArticlePartContent[] = [];
    h4: ArticlePartContent[] = [];
    h5: ArticlePartContent[] = [];
    h6: ArticlePartContent[] = [];
    questions: ArticleQuestion[] = [];
    images: Image[] = [];
    schemaJsonLd = '';
    eatAttribution: EatAttribution = emptyEatAttribution();

    polish: PolishState | null = "skipped";
    enhanced: EnhanceState | null = "pending";
    /** whole = one OpenAI call wrote the article; skip auto-polish. */
    aiGenerateMode: "whole" | "parts" | null = null;
    /** running | done | failed — frontend unlocks leftover FAQ/H when this leaves running. */
    aiGenerateStatus: "running" | "done" | "failed" | "review" | null = null;
    /** Path B proposals. The body stays original until a block is accepted. */
    blockProposals: BlockProposal[] = [];
    /** Fill topics that did not become a proposal, with a concrete discard reason. */
    fillDiscardedTopics: FillDiscardedTopic[] = [];

    id: string | null = null;
    created_at: number = Date.now();
    ai_content_last_generated_at: number | null = null;
    shareToken: string | null = null;

    static fromObject(value: any): Article {
        const a = new Article;
        for (const prop in a) {
            if (Object.prototype.hasOwnProperty.call(a, prop) && Object.prototype.hasOwnProperty.call(value, prop)) {
                (a as any)[prop] = value[prop];
            }
            if (!Object.prototype.hasOwnProperty.call('polish', value) && !value.polish) {
                a.polish = null;
            }
            type OldKeywordFormat = Keyword & {count: boolean};
            (a.allKeywords as Array<OldKeywordFormat>).forEach(keyword => {
                if (keyword.count !== undefined) {
                    keyword.checked = keyword.count;
                }
            });
        }
        a.isAnalyze = !!value.isAnalyze;
        a.geoTemplate = normalizeGeoTemplate(value.geoTemplate);
        a.geoTemplateUserPicked = !!value.geoTemplateUserPicked;
        a.tone = value.tone || 'professional';
        a.audience = value.audience || 'general';
        a.depth = value.depth || 'standard';
        a.secondaryKeywords = Array.isArray(value.secondaryKeywords) ? value.secondaryKeywords : [];
        a.improveGoal = value.improveGoal === 'polish' || value.improveGoal === 'fill' || value.improveGoal === 'rewrite'
            ? value.improveGoal
            : '';
        a.sectionUids = Array.isArray(value.sectionUids)
            ? value.sectionUids
                .map((item: unknown) => String(item || '').trim())
                .filter((id: string, index: number, list: string[]) => /^h2-\d+$/.test(id) && list.indexOf(id) === index)
            : [];
        a.blockProposals = normalizeBlockProposals(value.blockProposals);
        a.fillDiscardedTopics = normalizeFillDiscardedTopics(value.fillDiscardedTopics);
        a.aiWritingBrief = value.aiWritingBrief || null;
        a.eatAttribution = normalizeEatAttribution(value.eatAttribution);
        a.aiGenerateMode = value.aiGenerateMode === 'whole' || value.aiGenerateMode === 'parts'
            ? value.aiGenerateMode
            : (a.aiGenerateMode || null);
        a.aiGenerateStatus = value.aiGenerateStatus === 'running'
            || value.aiGenerateStatus === 'done'
            || value.aiGenerateStatus === 'failed'
            || value.aiGenerateStatus === 'review'
            ? value.aiGenerateStatus
            : (a.aiGenerateStatus || null);
        if (typeof value.aiPrompt === 'string') {
            a.aiPrompt = value.aiPrompt;
        }
        a.noAiPrompt = !!value.noAiPrompt;
        normalizeArticleInvisibleText(a);
        return a;
    }

    toObject(): Record<string, unknown> {
        // shareToken is written only by CreateShareLink / RevokeShareLink
        // (#30940). A tab loaded before Share has null here, and its next save
        // would null the live token, so GetSharedArticle answered 404.
        // fillDiscardedTopics is written only by the server when a Fill run
        // ends in review (#30947). The watcher does not copy it into the
        // loaded article, so a save in review would replace it with [] or
        // with the list of an earlier run.
        const ignore = ['loadedSeoScore', 'organicPosition', 'googleAIOPosition', 'chatGPTPosition', 'shareToken', 'fillDiscardedTopics'];

        const r: Record<string, unknown> = {};
        for (const prop in this) {
            if (Object.prototype.hasOwnProperty.call(this, prop) && !ignore.includes(prop)) {
                r[prop] = this[prop];
            }
        }
        r.isAnalyze = !!this.isAnalyze;
        r.geoTemplate = normalizeGeoTemplate(this.geoTemplate);
        r.tone = this.tone || 'professional';
        r.audience = this.audience || 'general';
        r.depth = this.depth || 'standard';
        r.secondaryKeywords = Array.isArray(this.secondaryKeywords) ? this.secondaryKeywords : [];
        // Empty values are written too. update() keeps any key it does not get,
        // so an omitted empty list brought the old proposals back on reload.
        r.improveGoal = this.improveGoal === 'polish' || this.improveGoal === 'fill' || this.improveGoal === 'rewrite'
            ? this.improveGoal
            : '';
        r.sectionUids = Array.isArray(this.sectionUids) ? this.sectionUids : [];
        r.blockProposals = Array.isArray(this.blockProposals) ? this.blockProposals : [];
        r.aiWritingBrief = this.aiWritingBrief || null;
        r.eatAttribution = normalizeEatAttribution(this.eatAttribution);
        // Firestore update() rejects an own property whose value is undefined
        // (console: UndefinedProperties). Drop those keys. null stays.
        return omitUndefinedFields(r) as Record<string, unknown>;
    }

    /**
     * Return all content parts (no metas and questions)
     */
    getAllParts(): ArticlePart[] {
        const ret = [];
        for (let i = 1; i <= 6; ++i) {
            ret.push({
                number: i,
                value: (this as any)['h' + i]
            });
        }
        return ret;
    }

    length(): number {
        return this.getAllParts()
            .map(part => part.value)
            .map(subparts => subparts
                .reduce((total, subpart) => total + subpart.h.length + subpart.p.length, 0)
            ).reduce((total, partLength) => total + partLength, 0);
    }
}

export class ArticleLoadings implements ArticleContent<boolean> {
    metaTitle = false;
    metaDescription = false;
    h1: ArticlePartContent<boolean>[] = [];
    h2: ArticlePartContent<boolean>[] = [];
    h3: ArticlePartContent<boolean>[] = [];
    h4: ArticlePartContent<boolean>[] = [];
    h5: ArticlePartContent<boolean>[] = [];
    h6: ArticlePartContent<boolean>[] = [];
    questions: ArticleQuestion<boolean>[] = [];
    loadingRatioTotal = 0;
    loadingRatioContent = 0;
    loadingRatioQuestions = 0;

    loadingCompleted = false;
    polishing = false;

    computeLoadingRatio(): void {
        let metaCount = 0;
        let metaLoading = 0;
        let contentCount = 0;
        let contentLoading = 0;
        let questionsCount = 0;
        let questionsLoading = 0;

        for (const meta of ['metaTitle', 'metaDescription']) {
            metaCount++;
            metaLoading += (this as any)[meta] ? 1 : 0;
        }

        for (let i = 1; i <= 6; ++i) {
            for (const subpart of (this as any)['h' + i]) {
                contentCount += 2;
                contentLoading += subpart && subpart.h ? 1 : 0;
                contentLoading += subpart && subpart.p ? 1 : 0;
            }
        }
        this.loadingRatioContent = contentCount ? contentLoading / contentCount : 0;

        for (const question of this.questions) {
            questionsCount += 2;
            questionsLoading += question?.question ? 1 : 0;
            questionsLoading += question?.answer ? 1 : 0;
        }

        this.loadingRatioQuestions = questionsCount ? questionsLoading / questionsCount : 0;
        const totalDenom = metaCount + contentCount + questionsCount;
        this.loadingRatioTotal = totalDenom
            ? (metaLoading + contentLoading + questionsLoading) / totalDenom
            : 0;
    }
}

export class ArticleWordCounts implements ArticleContent<number> {
    metaTitle = 0;
    metaDescription = 0;
    h1: ArticlePartContent<number>[] = [];
    h2: ArticlePartContent<number>[] = [];
    h3: ArticlePartContent<number>[] = [];
    h4: ArticlePartContent<number>[] = [];
    h5: ArticlePartContent<number>[] = [];
    h6: ArticlePartContent<number>[] = [];
    questions: ArticleQuestion<number>[] = [];

    getSum(): number {
        let sum = 0;

        // Do not count metas into length
        // for (const meta of ['metaTitle', 'metaDescription']) {
        //     sum += (this as any)[meta];
        // }

        for (let i = 1; i <= 6; ++i) {
            for (const subpart of (this as any)['h' + i]) {
                sum += subpart.h;
                sum += subpart.p;
            }
        }

        for (const question of this.questions) {
            sum += question.question;
            sum += question.answer;
        }

        return sum;
    }
}

export interface CancelArticleRequest {
    articleId: string;
    force?: boolean;
}

export interface ArticleInspirations {
    [site: string]: ScrapperResponse;
}

export interface ArticleMetaPartStorePath<T = string> {
    metaPart: ArticleMetaKey,
    value: T
}

export interface ArticleContentStorePath {
    contentPart: ArticleContentKey,
    index: number,
    aiLoading?: boolean,
    keepContent: boolean
}

export interface ArticleContentPartStorePath<T = string> {
    contentPart: ArticleContentKey,
    index: number,
    contentKey: ArticleContentTag,
    value: T
}

export interface ArticleQuestionStorePath {
    index: number,
    aiLoading?: boolean
}

export interface ArticleQuestionPartStorePath<T = string> {
    index: number,
    questionKey: ArticleQuestionTag,
    value: T
}

export type ArticlePartStorePath<T = string> =
    ArticleMetaPartStorePath<T>
    | ArticleContentPartStorePath<T>
    | ArticleQuestionPartStorePath<T>

export type ArticlesPage = {
    first: DocumentData,
    last: DocumentData,
    items: Article[]
}
